import React, { useState, useMemo, useEffect, useRef, useLayoutEffect } from 'react';
import { useLocation, useNavigate, useNavigationType, useSearchParams } from 'react-router-dom';
import usePortvilleRoomAuth from '../../../hooks/admin/usePortvilleRoomAuth';
import { usePortvilleRooms } from '../../../hooks/admin/usePortvilleRooms';
import { useAuth } from '../../../hooks/useAuth';
import RoomTable from '../../../components/admin/portville-rooms/RoomTable';
import RoomForm from '../../../components/admin/portville-rooms/RoomForm';
import RoomHistoryModal from '../../../components/admin/portville-rooms/RoomHistoryModal';
import MoveOutRequestModal from '../../../components/admin/portville-rooms/MoveOutRequestModal';
import StatusFilter from '../../../components/admin/portville-rooms/StatusFilter';
import ExpiryFilter from '../../../components/admin/portville-rooms/ExpiryFilter';
import { PortvilleRoomWithResidency } from '../../../types/portvilleRoom';
import { MoveOutService } from '../../../services/MoveOutService';
import { reviewPortvilleContractCancellation } from '../../../services/PortvilleContractCancellationService';
import { Timestamp, collection, doc, getDocs, query, updateDoc, where } from 'firebase/firestore';
import { db } from '../../../firebase';
import { getAdminScrollContainer, getAdminScrollMetrics, getAdminScrollTop, setAdminScrollTop } from '../../../utils/adminScroll';
import {
  PORTVILLE_ROOMS_BEFORE_FORM_SCROLL_KEY,
  PORTVILLE_ROOMS_LAST_SCROLL_KEY,
  PORTVILLE_ROOMS_RESTORE_ON_NEXT_KEY,
  PORTVILLE_ROOMS_SCROLL_KEY,
} from '../../../utils/portvilleRoomsScroll';

const PortvilleRoomManagePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { loading: authLoading, hasAccess } = usePortvilleRoomAuth();
  const { 
    rooms, 
    allRooms,
    pendingMoveOutRequests,
    loading,
    connected,
    error,
    searchTerm, 
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    expiryFilter,
    setExpiryFilter,
    addRoom, 
    updateRoom,
    refreshData
  } = usePortvilleRooms();

  const [showForm, setShowForm] = useState(false);
  const [editingRoom, setEditingRoom] = useState<PortvilleRoomWithResidency | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showUpdateToast, setShowUpdateToast] = useState(false);
  const [processingMoveOut, setProcessingMoveOut] = useState(false);
  const [processingContractCancellation, setProcessingContractCancellation] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedRoomForHistory, setSelectedRoomForHistory] = useState<PortvilleRoomWithResidency | undefined>(undefined);
  const [showMoveOutModal, setShowMoveOutModal] = useState(false);
  const [selectedMoveOutRequestId, setSelectedMoveOutRequestId] = useState<string>('');
  const [roomFormPresetStatus, setRoomFormPresetStatus] = useState<PortvilleRoomWithResidency['status'] | undefined>(undefined);

  // 스크롤 위치 저장용 ref (useState 대신 ref 사용으로 리렌더링 방지)
  const scrollPositionRef = useRef<number>(0);
  const isRestoringRef = useRef(false);
  const [pendingRestorePos, setPendingRestorePos] = useState<number | null>(null);

  const urlView = searchParams.get('view');
  const urlMode = searchParams.get('mode'); // add | edit | createContract
  const urlRoomId = searchParams.get('roomId');
  const urlPresetStatus = searchParams.get('presetStatus') as PortvilleRoomWithResidency['status'] | null;

  // 만료일 계산 헬퍼 함수
  const getDaysUntilExpiry = (endDate: Date) => {
    const today = new Date();
    const diffTime = endDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // 전체 호실 목록에서 상태별 카운트 계산
  const roomCounts = useMemo(() => {
    const counts = {
      all: allRooms.length,
      occupied: 0,
      preparing: 0,
      vacant: 0,
      contracting: 0,
      facility: 0,
      moveoutPending: 0,
      contractCancellationPending: 0,
      extensionPending: 0
    };

    allRooms.forEach((room: PortvilleRoomWithResidency & { hasPendingMoveOut?: boolean }) => {
      const { status } = room;
      if (status === 'occupied' || status === 'preparing' || status === 'vacant' || status === 'contracting') {
        counts[status]++;
      } else if (status === 'facility') {
        counts.facility++;
      }
      if (room.hasPendingMoveOut) {
        counts.moveoutPending++;
      }
      if (room.pendingContractCancellation) {
        counts.contractCancellationPending++;
      }
      if (room.extensionStatus === 'applied' || room.secondExtensionStatus === 'applied') {
        counts.extensionPending++;
      }
    });

    return counts;
  }, [allRooms]);

  // 만료일별 카운트 계산
  const expiryCounts = useMemo(() => {
    const occupiedRooms = allRooms.filter(room => 
      room.status === 'occupied' && room.occupancyPeriod?.endDate
    );

    const counts = {
      all: occupiedRooms.length,
      expired: 0,
      expiringSoon: 0,
      expiringMonth: 0
    };

    occupiedRooms.forEach((room: PortvilleRoomWithResidency) => {
      if (room.occupancyPeriod?.endDate) {
        const daysUntilExpiry = getDaysUntilExpiry(room.occupancyPeriod.endDate);
        
        if (daysUntilExpiry < 0) {
          counts.expired++;
        } else if (daysUntilExpiry <= 7) {
          counts.expiringSoon++;
        } else if (daysUntilExpiry <= 30) {
          counts.expiringMonth++;
        }
      }
    });

    return counts;
  }, [allRooms]);

  // 브라우저 기본 스크롤 복원 비활성화
  useEffect(() => {
    // 브라우저의 기본 스크롤 복원 기능 비활성화
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    // 컴포넌트 언마운트 시 원래대로 복원
    return () => {
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'auto';
      }
    };
  }, []);

  // 초기 로드 완료 처리
  useEffect(() => {
    if (connected && !loading && allRooms.length > 0 && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [connected, loading, allRooms.length, isInitialLoad]);

  // 실시간 업데이트 토스트 (초기 로드 이후에만)
  useEffect(() => {
    if (connected && !loading && !isInitialLoad && allRooms.length > 0) {
      setShowUpdateToast(true);
      const timer = setTimeout(() => setShowUpdateToast(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [allRooms, connected, loading, isInitialLoad]);

  // URL 기반으로 폼/목록 뷰 동기화 (history에 남게 해서 브라우저 back이 의도대로 동작하도록)
  useEffect(() => {
    const shouldShowForm = urlView === 'form';
    if (shouldShowForm !== showForm) {
      setShowForm(shouldShowForm);
    }
  }, [urlView, showForm]);

  // URL 파라미터로 편집 대상/프리셋 동기화
  useEffect(() => {
    if (urlView !== 'form') return;

    if (urlMode === 'add') {
      setEditingRoom(undefined);
      setRoomFormPresetStatus(undefined);
      return;
    }

    if (!urlRoomId) {
      setEditingRoom(undefined);
      setRoomFormPresetStatus(undefined);
      return;
    }

    const found = allRooms.find(r => r.id === urlRoomId);
    setEditingRoom(found);

    if (urlMode === 'createContract') {
      setRoomFormPresetStatus(urlPresetStatus || 'occupied');
    } else {
      setRoomFormPresetStatus(undefined);
    }
  }, [allRooms, urlMode, urlPresetStatus, urlRoomId, urlView]);

  // 폼 표시 상태 변경 시 스크롤 처리 (지연 복원 방식)
  useEffect(() => {
    if (showForm) {
      // 폼으로 이동할 때는 항상 맨 위로
      setAdminScrollTop(0);
      return;
    }
    // 목록으로 돌아올 때는 즉시 스크롤하지 말고, 렌더가 안정된 뒤 복원하도록 예약
    const savedPosition = scrollPositionRef.current;
    if (savedPosition >= 0) {
      setPendingRestorePos(savedPosition);
    }
  }, [showForm]);

  // 목록 화면에서 스크롤 위치를 지속적으로 저장 (상세 페이지에서 "목록으로"로 복귀할 때도 복원 가능)
  useEffect(() => {
    if (showForm) return;
    const container = getAdminScrollContainer();
    if (!container) return;

    let rafId = 0;
    const onScroll = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        sessionStorage.setItem(PORTVILLE_ROOMS_LAST_SCROLL_KEY, String(container.scrollTop));
      });
    };

    container.addEventListener('scroll', onScroll, { passive: true });
    sessionStorage.setItem(PORTVILLE_ROOMS_LAST_SCROLL_KEY, String(container.scrollTop));

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      container.removeEventListener('scroll', onScroll);
    };
  }, [showForm]);

  // 페이지(다른 라우트)에서 복귀 시 스크롤 위치 복원 예약
  useEffect(() => {
    if (showForm) return;
    const restoreRequested = Boolean((location.state as any)?.restoreScroll);
    const restoreFlag = sessionStorage.getItem(PORTVILLE_ROOMS_RESTORE_ON_NEXT_KEY) === '1';

    const saved = sessionStorage.getItem(PORTVILLE_ROOMS_SCROLL_KEY);
    if (saved) {
      const pos = parseInt(saved, 10);
      if (!Number.isNaN(pos)) {
        setPendingRestorePos(pos);
        sessionStorage.setItem(PORTVILLE_ROOMS_LAST_SCROLL_KEY, String(pos));
      }
      return;
    }

    const beforeForm = sessionStorage.getItem(PORTVILLE_ROOMS_BEFORE_FORM_SCROLL_KEY);
    if (beforeForm) {
      const pos = parseInt(beforeForm, 10);
      if (!Number.isNaN(pos)) {
        setPendingRestorePos(pos);
        sessionStorage.setItem(PORTVILLE_ROOMS_LAST_SCROLL_KEY, String(pos));
      }
      return;
    }

    const shouldRestoreLast =
      restoreRequested || restoreFlag || (navigationType === 'POP' && Boolean(saved || beforeForm));

    if (!shouldRestoreLast) return;

    const last = sessionStorage.getItem(PORTVILLE_ROOMS_LAST_SCROLL_KEY);
    if (!last) return;
    const pos = parseInt(last, 10);
    if (!Number.isNaN(pos)) setPendingRestorePos(pos);
  }, [location.state, navigationType, showForm]);

  // 실제 스크롤 복원 수행 (컨텐츠 높이가 충분해질 때까지 대기)
  useLayoutEffect(() => {
    if (pendingRestorePos == null) return;
    if (showForm) return; // 폼 화면에서는 복원하지 않음
    if (loading) return;  // 데이터 로딩 중에는 복원하지 않음

    isRestoringRef.current = true;
    const target = pendingRestorePos;
    const startedAt = performance.now();
    const MAX_WAIT_MS = 800; // 최대 대기 시간

    const tryRestore = () => {
      const { scrollHeight, viewportHeight } = getAdminScrollMetrics();
      const maxScroll = Math.max(0, scrollHeight - viewportHeight);
      const elapsed = performance.now() - startedAt;

      if (maxScroll >= target || elapsed > MAX_WAIT_MS) {
        // 가능한 한 정확히 복원 (초과 시 바닥으로 고정되는 것을 방지하기 위해 clamp)
        setAdminScrollTop(Math.min(target, maxScroll));
        isRestoringRef.current = false;
        setPendingRestorePos(null);
        sessionStorage.removeItem(PORTVILLE_ROOMS_SCROLL_KEY);
        sessionStorage.removeItem(PORTVILLE_ROOMS_BEFORE_FORM_SCROLL_KEY);
        sessionStorage.removeItem(PORTVILLE_ROOMS_RESTORE_ON_NEXT_KEY);
      } else {
        requestAnimationFrame(tryRestore);
      }
    };

    // 다음 페인트 이후 시도
    requestAnimationFrame(tryRestore);
  }, [pendingRestorePos, showForm, loading, rooms.length]);

  if (authLoading) {
    return (
      <div className="w-full min-h-screen bg-[#f7f9fd]">
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#155acb]"></div>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return null;
  }

  if (isInitialLoad || (loading && allRooms.length === 0)) {
    return (
      <div className="w-full min-h-screen bg-[#f7f9fd]">
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#155acb]"></div>
        </div>
      </div>
    );
  }

  const handleAddRoom = () => {
    // 현재 스크롤 위치 저장
    scrollPositionRef.current = getAdminScrollTop();
    sessionStorage.setItem(PORTVILLE_ROOMS_BEFORE_FORM_SCROLL_KEY, String(scrollPositionRef.current));
    setSearchParams({ view: 'form', mode: 'add' }, { replace: false });
  };

  const handleEditRoom = (room: PortvilleRoomWithResidency) => {
    // 현재 스크롤 위치 저장
    scrollPositionRef.current = getAdminScrollTop();
    sessionStorage.setItem(PORTVILLE_ROOMS_BEFORE_FORM_SCROLL_KEY, String(scrollPositionRef.current));
    setSearchParams({ view: 'form', mode: 'edit', roomId: room.id }, { replace: false });
  };

  const handleCreateContract = (room: PortvilleRoomWithResidency) => {
    scrollPositionRef.current = getAdminScrollTop();
    sessionStorage.setItem(PORTVILLE_ROOMS_BEFORE_FORM_SCROLL_KEY, String(scrollPositionRef.current));
    // 관리자 수기 계약 등록: 입주점검/날인 없이 바로 입주중(과거/협의 계약) 기록 생성
    setSearchParams(
      { view: 'form', mode: 'createContract', roomId: room.id, presetStatus: 'occupied' },
      { replace: false }
    );
  };

  const handleFormSubmit = async (formData: any) => {
    setIsSubmitting(true);
    try {
      let success = false;
      if (editingRoom) {
        success = await updateRoom(editingRoom.id, formData);
      } else {
        success = await addRoom(formData);
      }
      
      if (success) {
        setSearchParams({}, { replace: true });
      }
      return success;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFormCancel = () => {
    setSearchParams({}, { replace: true });
  };

  const handleContractRoom = (room: PortvilleRoomWithResidency) => {
    // 페이지 이동 전 스크롤 위치를 sessionStorage에 저장
    sessionStorage.setItem(PORTVILLE_ROOMS_SCROLL_KEY, String(getAdminScrollTop()));
    navigate(`/admin/portville-rooms/contract/${room.id}`);
  };

  const handleViewContract = (
    room: PortvilleRoomWithResidency,
    contractType?: 'original' | 'extension1' | 'extension2'
  ) => {
    // 페이지 이동 전 스크롤 위치를 sessionStorage에 저장
    sessionStorage.setItem(PORTVILLE_ROOMS_SCROLL_KEY, String(getAdminScrollTop()));
    const baseUrl = `/admin/portville-rooms/view-contract/${room.id}`;
    const url = contractType ? `${baseUrl}?type=${contractType}` : baseUrl;
    navigate(url);
  };

  const handleMoveInInspection = (room: PortvilleRoomWithResidency) => {
    // 페이지 이동 전 스크롤 위치를 sessionStorage에 저장
    sessionStorage.setItem(PORTVILLE_ROOMS_SCROLL_KEY, String(getAdminScrollTop()));
    navigate(`/admin/portville-rooms/move-in-inspection/${room.id}`);
  };

  const handleMoveOutInspection = (room: PortvilleRoomWithResidency) => {
    sessionStorage.setItem(PORTVILLE_ROOMS_SCROLL_KEY, String(getAdminScrollTop()));
    navigate(`/admin/portville-rooms/move-out-inspection/${room.id}`);
  };

	  const handleTerminateContract = async (room: PortvilleRoomWithResidency) => {
    if (!room.currentResidencyId) {
      alert('현재 입주 기록이 없습니다.');
      return;
    }
    if (!room.moveOutInspectionSignedAt) {
      alert('퇴거점검이 완료되지 않았습니다.');
      return;
    }

    const confirmed = window.confirm('계약을 종료하고 방 상태를 준비중으로 변경하시겠습니까? 해당 입주의 미처리 퇴거신청도 함께 종결됩니다.');
    if (!confirmed) return;

	    try {
	      const now = Timestamp.now();
        await MoveOutService.terminateResidency(room.id, room.currentResidencyId);

        // Defensive: stale lottery archiving (best effort)
        try {
          const contractorUid = (room as any).residentUid as string | undefined;
          if (contractorUid) {
            const getMillis = (value: any): number => {
              try {
                if (!value) return 0;
                if (typeof value.toMillis === 'function') return value.toMillis();
                if (typeof value.toDate === 'function') return value.toDate().getTime();
                if (value instanceof Date) return value.getTime();
                const date = new Date(value);
                return Number.isNaN(date.getTime()) ? 0 : date.getTime();
              } catch {
                return 0;
              }
            };

            const lotteryRef = collection(db, 'portville_lottery_history');
            const fetchLatest = async (source: 'roomId' | 'roomNumber') => {
              const snapshot =
                source === 'roomId'
                  ? await getDocs(query(lotteryRef, where('roomId', '==', room.id), where('uid', '==', contractorUid)))
                  : await getDocs(
                      query(lotteryRef, where('roomNumber', '==', String((room as any).roomNumber || '')), where('uid', '==', contractorUid))
                    );
              if (snapshot.empty) return null;
              const docs = snapshot.docs
                .map((snap) => ({ id: snap.id, data: snap.data() as any }))
                .sort((a, b) => getMillis(b.data.drawnAt) - getMillis(a.data.drawnAt));
              return docs[0] || null;
            };

            const latest = (await fetchLatest('roomId')) ?? (await fetchLatest('roomNumber'));
            if (latest) {
              const status = String(latest.data?.status || '');
              if (status === 'contracted' || status === 'cancelled') {
                await updateDoc(doc(db, 'portville_lottery_history', latest.id), {
                  archivedAt: latest.data?.archivedAt || now,
                  archivedReason: latest.data?.archivedReason || status
                });
              }
            }
          }
        } catch (lotteryErr) {
          console.warn('계약종료 후 lottery 정리(방어) 실패:', lotteryErr);
        }

	      alert('계약이 종료되었습니다. 방 상태가 준비중으로 변경되었습니다.');
	      refreshData();
	    } catch (e) {
      console.error('계약종료 처리 실패:', e);
      alert('계약종료 처리 중 오류가 발생했습니다.');
    }
  };

  const handleViewHistory = (room: PortvilleRoomWithResidency) => {
    setSelectedRoomForHistory(room);
    setShowHistoryModal(true);
  };

  const handleExtensionRequest = (room: PortvilleRoomWithResidency, round: 1 | 2) => {
    // 페이지 이동 전 스크롤 위치를 sessionStorage에 저장
    sessionStorage.setItem(PORTVILLE_ROOMS_SCROLL_KEY, String(getAdminScrollTop()));
    navigate(`/admin/portville-rooms/extension-request/${room.id}?round=${round}`);
  };

  const handleExtensionContract = (room: PortvilleRoomWithResidency, round: 1 | 2) => {
    // 페이지 이동 전 스크롤 위치를 sessionStorage에 저장
    sessionStorage.setItem(PORTVILLE_ROOMS_SCROLL_KEY, String(getAdminScrollTop()));
    navigate(`/admin/portville-rooms/extension-contract/${room.id}?round=${round}`);
  };

  const handleCloseHistoryModal = () => {
    setShowHistoryModal(false);
    setSelectedRoomForHistory(undefined);
  };

  const handleMoveOutAction = async (action: 'detail' | 'approve' | 'reject', requestId: string) => {
    if (!user?.uid) {
      alert('관리자 인증이 필요합니다.');
      return;
    }

    if (action === 'detail') {
      setSelectedMoveOutRequestId(requestId);
      setShowMoveOutModal(true);
    } else if (action === 'approve') {
      const confirmed = window.confirm('퇴거신청을 승인하시겠습니까?');
      if (!confirmed) return;

      setProcessingMoveOut(true);
      try {
        await MoveOutService.approveMoveOutRequest(requestId, user.uid);
        alert('퇴거신청이 승인되었습니다. 계약 종료일이 변경되었습니다.');
        handleMoveOutProcessed(false);
      } catch (error) {
        console.error('퇴거신청 승인 실패:', error);
        alert('퇴거신청 승인에 실패했습니다.');
      } finally {
        setProcessingMoveOut(false);
      }
    } else if (action === 'reject') {
      const reason = prompt('반려 사유를 입력해주세요:');
      if (!reason?.trim()) {
        alert('반려 사유를 입력해주세요.');
        return;
      }

      setProcessingMoveOut(true);
      try {
        await MoveOutService.rejectMoveOutRequest(requestId, user.uid, reason);
        alert('퇴거신청이 반려되었습니다.');
        handleMoveOutProcessed(false);
      } catch (error) {
        console.error('퇴거신청 반려 실패:', error);
        alert('퇴거신청 반려에 실패했습니다.');
      } finally {
        setProcessingMoveOut(false);
      }
    }
  };

  const handleReviewContractCancellation = async (
    room: PortvilleRoomWithResidency,
    action: 'approve' | 'reject'
  ) => {
    const cancellation = room.pendingContractCancellation;
    if (!cancellation?.lotteryId) {
      alert('포기신청 정보를 찾을 수 없습니다.');
      return;
    }

    let reviewMemo = '';
    if (action === 'approve') {
      const confirmed = window.confirm(
        `${room.roomNumber}호 계약 포기신청을 승인하시겠습니까?\n\n승인 시 호실은 공실로 전환되고 해당 추첨은 취소 처리됩니다.`
      );
      if (!confirmed) return;
    } else {
      const reason = window.prompt('포기신청 반려 사유를 입력해주세요:');
      if (!reason?.trim()) {
        alert('반려 사유를 입력해주세요.');
        return;
      }
      reviewMemo = reason.trim();
    }

    setProcessingContractCancellation(true);
    try {
      await reviewPortvilleContractCancellation({
        lotteryId: cancellation.lotteryId,
        action,
        reviewMemo
      });
      alert(action === 'approve' ? '계약 포기신청이 승인되었습니다.' : '계약 포기신청이 반려되었습니다.');
      refreshData();
      setTimeout(() => {
        refreshData();
      }, 1500);
    } catch (error: any) {
      console.error('계약 포기신청 검토 실패:', error);
      alert(error?.message || '계약 포기신청 처리 중 오류가 발생했습니다.');
    } finally {
      setProcessingContractCancellation(false);
    }
  };

  const handleCloseMoveOutModal = () => {
    setShowMoveOutModal(false);
    setSelectedMoveOutRequestId('');
  };

  const handleMoveOutProcessed = (closeModal: boolean = true) => {
    if (closeModal) {
      handleCloseMoveOutModal();
    }

    if (showForm && editingRoom) {
      setShowForm(false);
      setEditingRoom(undefined);
    }

    refreshData();
    setTimeout(() => {
      refreshData();
    }, 1500);

    setShowUpdateToast(true);
    const timer = setTimeout(() => setShowUpdateToast(false), 3000);
    return () => clearTimeout(timer);
  };

  return (
    <div className="w-full min-h-screen bg-[#f7f9fd]">
      {/* 실시간 업데이트 토스트 */}
      {showUpdateToast && (
        <div className="fixed top-4 right-4 z-50 bg-[#155acb] text-white px-4 py-2 rounded-sm shadow-lg flex items-center space-x-2 animate-fade-in-out">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          <span className="text-sm font-['Pretendard']">데이터가 업데이트되었습니다</span>
        </div>
      )}
      {processingContractCancellation && (
        <div className="fixed top-16 right-4 z-50 bg-red-700 text-white px-4 py-2 rounded-sm shadow-lg">
          <span className="text-sm font-['Pretendard']">계약 포기신청 처리 중...</span>
        </div>
      )}

      {/* 본문 컨테이너 */}
      <div className="max-w-[1600px] mx-auto relative pt-8 px-3 lg:px-6">
        {/* 헤더 섹션 */}
        <div className="mt-8">
          <div className="flex items-center justify-between">
            <div className="text-slate-800 text-4xl font-extrabold font-['Pretendard'] leading-9">
              포트빌 호실관리
            </div>
            <div className="flex items-center space-x-2 text-sm">
              <div className="flex items-center space-x-1">
                <div className={`w-2 h-2 rounded-full ${
                  loading ? 'bg-yellow-400' : 
                  connected ? 'bg-green-400' : 
                  'bg-red-400'
                } ${loading || !connected ? 'animate-pulse' : ''}`}></div>
                <span className="text-[#6b7684] font-['Pretendard']">
                  {loading ? '연결 중...' : 
                   connected ? '실시간 연결됨' : 
                   '연결 끊김'}
                </span>
              </div>
            </div>
          </div>
          <div className="w-full h-px mt-8 outline outline-1 outline-offset-[-0.50px] outline-neutral-200"></div>
        </div>

        {/* 안내 메시지 */}
        <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
          <div className="p-4 lg:p-6 bg-[#f2f6fb] border-l-4 border-[#155acb] rounded-r-sm">
            <p className="text-[#1e2c49] text-base lg:text-lg font-medium font-['Pretendard']">
              포트빌 호실 현황 및 관리를 위한 페이지입니다.
            </p>
          </div>
        </div>

        {/* 오류 메시지 표시 */}
        {error && (
          <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
            <div className="p-4 lg:p-6 bg-red-50 border-l-4 border-red-500 rounded-r-sm">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-800 font-['Pretendard']">{error}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {!showForm ? (
          <>
            {/* 필터 및 검색 섹션 */}
            <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
              <div className="p-4 lg:p-8">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9">
                    필터 및 관리
                  </h2>
                  <button
                    onClick={handleAddRoom}
                    className="w-[100px] h-11 bg-[#155acb] text-white text-base font-bold font-['Pretendard'] rounded-sm hover:bg-[#155acb]/90 transition-colors"
                  >
                    호실 추가
                  </button>
                </div>
                <div className="hidden lg:block w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-6"></div>
                <div className="lg:hidden w-full h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-4"></div>
                
                <div className="grid grid-cols-1 lg:grid-cols-[2fr_2fr_1.5fr] gap-8">
                  <div>
                    <h3 className="text-[#1e2c49] text-lg font-semibold font-['Pretendard'] mb-4">
                      호실 상태
                    </h3>
                    <StatusFilter
                      currentFilter={statusFilter}
                      onFilterChange={setStatusFilter}
                      roomCounts={roomCounts}
                    />
                  </div>
                  
                  <div>
                    <h3 className="text-[#1e2c49] text-lg font-semibold font-['Pretendard'] mb-4">
                      계약 만료일
                    </h3>
                    <ExpiryFilter
                      currentFilter={expiryFilter}
                      onFilterChange={setExpiryFilter}
                      expiryCounts={expiryCounts}
                    />
                  </div>

                  <div>
                    <h3 className="text-[#1e2c49] text-lg font-semibold font-['Pretendard'] mb-4">
                      호실 검색
                    </h3>
                    <input
                      type="text"
                      placeholder="호실번호, 입주자명, 연락처, 소속 등"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-3 py-3 text-[#1e2c49] font-['Pretendard'] text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* 호실 목록 */}
            <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
              <div className="p-4 lg:p-8">
                <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9 mb-4">
                  호실 목록
                </h2>
                <div className="hidden lg:block w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-6"></div>
                <div className="lg:hidden w-full h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-4"></div>

                <RoomTable
                  rooms={rooms}
                  onEdit={handleEditRoom}
                  onCreateContract={handleCreateContract}
                  onContract={handleContractRoom}
                  onViewContract={handleViewContract}
	                  onMoveInInspection={handleMoveInInspection}
	                  onMoveOutInspection={handleMoveOutInspection}
	                  onReviewContractCancellation={handleReviewContractCancellation}
	                  onTerminateContract={handleTerminateContract}
                  onViewHistory={handleViewHistory}
                  onExtensionRequest={handleExtensionRequest}
                  onExtensionContract={handleExtensionContract}
                  loading={loading}
                />
              </div>
            </div>
          </>
        ) : (
          /* 호실 추가/수정 폼 */
          <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
            <div className="p-4 lg:p-8">
              <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9 mb-4">
                {editingRoom ? '호실 수정' : '호실 추가'}
              </h2>
              <div className="hidden lg:block w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-6"></div>
              <div className="lg:hidden w-full h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-4"></div>
              
              <RoomForm
                room={editingRoom}
                presetStatus={roomFormPresetStatus}
                onSubmit={handleFormSubmit}
                onCancel={handleFormCancel}
                onViewContract={handleViewContract}
                onMoveOutAction={handleMoveOutAction}
                isSubmitting={isSubmitting}
	                processingMoveOut={processingMoveOut}
              />
            </div>
          </div>
        )}

        {/* 하단 여백 */}
        <div className="h-16"></div>
      </div>

      {/* 입주 히스토리 모달 */}
      {selectedRoomForHistory && (
        <RoomHistoryModal
          room={selectedRoomForHistory}
          isOpen={showHistoryModal}
          onClose={handleCloseHistoryModal}
        />
      )}

      {/* 퇴거신청 상세 모달 */}
      {user?.uid && selectedMoveOutRequestId && (
        <MoveOutRequestModal
          isOpen={showMoveOutModal}
          onClose={handleCloseMoveOutModal}
          requestId={selectedMoveOutRequestId}
          adminUid={user.uid}
          onProcessed={() => handleMoveOutProcessed(true)}
        />
      )}
    </div>
  );
};

export default PortvilleRoomManagePage;
